import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Skeleton,
  Alert,
  Grid,
  Modal,
  ModalDialog,
  ModalClose,
  Divider,
  IconButton,
  Snackbar,
} from '@mui/joy';
import { Container } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAuth } from '../../main/context/AuthContext';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { getSiteDisplayName } from '../../main/utils/siteDisplayName';
import { primaryColor } from '../../main/theme/constants';
import { getCustomerDashboard, bulkUpdateReceiverDetails, regenerateCustomerInvoices } from '../../main/API/customer';
import { useNavigate } from 'react-router-dom';
import { formatPaymentStatus, getPaymentStatusChipStyle } from '../../main/widgets/PaymentStatusOptionList';
import { labelForEventTypeCode } from '../../main/utils/trackingEventLabels';
import { OmrAmount } from '../../main/components/OmrAmount';
import { getLocalizedUserFullName } from '../../main/utils/userDisplayName';
import { translateApiErrorData } from '../../main/utils/masterApiError';
import { translateAggregateBackendMessage } from '../../main/utils/backendErrorTranslation';
import { dedupeRequest } from '../../main/utils/dedupeRequest';
import CustomerDeliveryDestinationForm from './widgets/CustomerDeliveryDestinationForm';
import CustomerInvoicePayActions from './widgets/CustomerInvoicePayActions';
import { formatCustomerDestinationTypeLabel } from '../utils/customerDestinationTypes';
import {
  formatCustomerShipmentUnitDate,
  getCustomerShipmentUnit,
  getCustomerShipmentUnitSerial,
  isCustomerShipmentUnitFieldVisible,
  useCustomerShipmentUnitVisibleFields,
} from '../utils/customerShipmentUnit';

// Get shipment count for a status from summary (supports by_status object, by_status array, or direct keys)
function getShipmentStatusCount(summary, statusKey) {
  if (!summary) return 0;
  const snake = statusKey.replace(/\s+/g, '_');
  // API returns by_status as object: { "shipping soon": 0, "shipped": 4, "arrived": 3, ... }
  const byStatus = summary.by_status;
  if (byStatus && typeof byStatus === 'object' && !Array.isArray(byStatus)) {
    const count = byStatus[statusKey] != null ? byStatus[statusKey] : byStatus[snake];
    if (count != null) return Number(count) || 0;
  }
  if (Array.isArray(byStatus)) {
    const item = byStatus.find(
      (s) => String(s.status || s).toLowerCase().replace(/\s+/g, '_') === snake
    );
    return item && (item.count != null || item.total != null)
      ? Number(item.count != null ? item.count : item.total) || 0
      : 0;
  }
  if (summary[statusKey] != null) return Number(summary[statusKey]) || 0;
  if (summary[snake] != null) return Number(summary[snake]) || 0;
  return 0;
}

const SHIPMENT_STATUS_KEYS = [
  'shipping soon',
  'shipped',
  'arrived',
  'out for delivery',
  'delivered',
];

// Chip style by shipment status (for Recent shipments)
function shipmentStatusChipStyle(status) {
  const styles = {
    shipping_soon: { bg: '#f1f5f9', color: '#64748b' },
    'shipping soon': { bg: '#f1f5f9', color: '#64748b' },
    shipped: { bg: '#eef2ff', color: '#4f46e5' },
    arrived: { bg: '#eff6ff', color: '#2563eb' },
    out_for_delivery: { bg: '#fffbeb', color: '#d97706' },
    'out for delivery': { bg: '#fffbeb', color: '#d97706' },
    delivered: { bg: '#ecfdf5', color: '#059669' },
  };
  const key = status && String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const withSpaces = key && key.replace(/_/g, ' ');
  return styles[key] || styles[withSpaces] || { bg: '#f1f5f9', color: '#64748b' };
}

// Format date for display (e.g. "Jan 30, 2025" or "30 Jan 2025")
function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) {
    return '—';
  }
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '—';
  }
}

/** True if delivery location cannot be updated (out for delivery, delivered). */
function cannotUpdateDeliveryLocation(status) {
  if (!status) return false;
  const key = String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const normalized = key.replace(/_/g, ' ');
  return ['out for delivery', 'delivered'].includes(normalized) || ['out_for_delivery', 'delivered'].includes(key);
}

/**
 * True if invoice is eligible for regeneration (same as API).
 * Eligible when: paid = false, and (payment_link empty, or payment_link_expire_at passed,
 * or payment link is Thawani checkout with expire_at null).
 */
function needsRegenerate(inv) {
  if (!inv) return false;
  const link = inv.payment_link != null ? String(inv.payment_link).trim() : '';
  if (!link) return true;
  const expireAt = inv.payment_link_expire_at;
  // Thawani checkout links with no expiry: show Regenerate (e.g. checkout.thawani.om)
  if (link.includes('checkout.thawani') && (expireAt == null || expireAt === '')) return true;
  if (!expireAt) return false;
  const d = new Date(expireAt);
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
}

export default function CustomerHomeScreen() {
  const { t, i18n } = useTranslation('ui');
  const { user, logout } = useAuth();
  const tRef = useRef(t);
  const logoutRef = useRef(logout);
  tRef.current = t;
  logoutRef.current = logout;
  const accountInactive = Boolean(user && user.is_active === false);
  const { settings } = useSiteSettings();
  const unitVisibleFields = useCustomerShipmentUnitVisibleFields();
  const siteName = getSiteDisplayName(settings, i18n.language);
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quickViewShipment, setQuickViewShipment] = useState(null);
  const [singleDeliveryShipment, setSingleDeliveryShipment] = useState(null);
  const [singleDeliveryType, setSingleDeliveryType] = useState('home');
  const [singleDeliveryLocation, setSingleDeliveryLocation] = useState('');
  const [singleDeliverySubmitting, setSingleDeliverySubmitting] = useState(false);
  const [singleDeliveryResult, setSingleDeliveryResult] = useState(null);
  const [deliverySuccessMessage, setDeliverySuccessMessage] = useState(null);
  const [regeneratingInvoiceId, setRegeneratingInvoiceId] = useState(null);
  const [regenerateSnackbar, setRegenerateSnackbar] = useState({ open: false, message: '', color: 'neutral' });
  const [openPaymentLinkAfterRefresh, setOpenPaymentLinkAfterRefresh] = useState(null);

  const formatStatus = useCallback(
    (status) => {
      if (!status) return '—';
      const key = String(status).toLowerCase().trim().replace(/\s+/g, '_');
      const withSpaces = key.replace(/_/g, ' ');
      const map = {
        shipping_soon: 'shipmentSoon',
        'shipping soon': 'shipmentSoon',
        shipped: 'shipmentShipped',
        arrived: 'shipmentArrived',
        out_for_delivery: 'shipmentOutForDelivery',
        'out for delivery': 'shipmentOutForDelivery',
        delivered: 'shipmentDelivered',
      };
      const k = map[key] || map[withSpaces];
      if (k) return t(`customer.${k}`);
      return status;
    },
    [t]
  );

  const formatDestinationLocationType = useCallback(
    (value) => formatCustomerDestinationTypeLabel(value, t, siteName),
    [t, siteName]
  );

  const fetchDashboard = useCallback(async ({ dedupeKey, isCancelled } = {}) => {
    if (isCancelled && isCancelled()) return;
    if (accountInactive) {
      setLoading(false);
      setDashboard(null);
      setError(null);
      return;
    }
    setError(null);
    setLoading(true);
    const doFetch = () => getCustomerDashboard();
    const response = dedupeKey
      ? await dedupeRequest(dedupeKey, doFetch)
      : await doFetch();
    if (isCancelled && isCancelled()) return;
    setLoading(false);

    if (!response) {
      setError(tRef.current('customer.loadDashboardError'));
      return;
    }
    if (response.status === 401) {
      logoutRef.current();
      return;
    }
    if (response.status >= 400) {
      setError((response.data && translateApiErrorData(response.data, tRef.current)) || tRef.current('customer.loadDashboardFailed'));
      return;
    }
    setDashboard(response.data);
  }, [accountInactive]);

  const fetchDashboardRef = useRef(fetchDashboard);
  fetchDashboardRef.current = fetchDashboard;

  useEffect(() => {
    let cancelled = false;
    fetchDashboardRef.current({ dedupeKey: 'customer/dashboard', isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [accountInactive]);

  // After regenerate, open the new payment link in a new tab when dashboard has refreshed
  useEffect(() => {
    if (openPaymentLinkAfterRefresh == null || !dashboard) return;
    const summary = dashboard.financial_summary || {};
    const unpaid = summary.unpaid_invoices || [];
    const recent = summary.recent_invoices || [];
    const inv = unpaid.find((i) => i.id === openPaymentLinkAfterRefresh) || recent.find((i) => i.id === openPaymentLinkAfterRefresh);
    // Prefer shared invoice.payment_link only — never substitute a separate-group URL.
    const link = inv && inv.payment_link && String(inv.payment_link).trim();
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
      setOpenPaymentLinkAfterRefresh(null);
    }
  }, [dashboard, openPaymentLinkAfterRefresh]);

  const handleRegenerateInvoice = async (invoiceId) => {
    if (!invoiceId) return;
    setRegeneratingInvoiceId(invoiceId);
    setRegenerateSnackbar({ open: false });
    const response = await regenerateCustomerInvoices([invoiceId]);
    setRegeneratingInvoiceId(null);
    if (!response) {
      setRegenerateSnackbar({ open: true, message: t('customer.paymentLinkError'), color: 'danger' });
      return;
    }
    if (response.status === 401) {
      logout();
      return;
    }
    if (response.status >= 400) {
      const msg =
        (response.data && translateApiErrorData(response.data, t)) || t('customer.paymentLinkFailed');
      setRegenerateSnackbar({ open: true, message: msg, color: 'danger' });
      return;
    }
    const count = response.data && response.data.regenerated_invoice_count != null ? response.data.regenerated_invoice_count : 0;
    setRegenerateSnackbar({
      open: true,
      message: count > 0 ? t('customer.invoicesUpdated', { count }) : t('customer.noNewPaymentLinks'),
      color: 'success',
    });
    fetchDashboard();
  };

  const openSingleDeliveryModal = (shipment) => {
    setSingleDeliveryShipment(shipment);
    setSingleDeliveryType(shipment.receiver_details && shipment.receiver_details.destination_location_type ? String(shipment.receiver_details.destination_location_type).toLowerCase() : 'home');
    setSingleDeliveryLocation(shipment.receiver_details && shipment.receiver_details.destination_location ? String(shipment.receiver_details.destination_location) : '');
    setSingleDeliveryResult(null);
  };

  const handleSingleDeliverySubmit = async () => {
    if (!singleDeliveryShipment) return;
    setSingleDeliverySubmitting(true);
    setSingleDeliveryResult(null);
    const response = await bulkUpdateReceiverDetails([singleDeliveryShipment.id], singleDeliveryType, singleDeliveryLocation);
    setSingleDeliverySubmitting(false);
    if (!response) {
      setSingleDeliveryResult({ error: t('customer.requestFailed') });
      return;
    }
    if (response.status >= 400) {
      let errMsg = '';
      if (response.data) {
        if (Array.isArray(response.data)) {
          const raw = response.data.map((e) => e.msg).filter(Boolean).join('; ');
          errMsg = raw ? translateAggregateBackendMessage(raw, t) : '';
        } else {
          errMsg = translateApiErrorData(response.data, t);
        }
      }
      setSingleDeliveryResult({ error: errMsg || t('customer.validationError') });
      return;
    }
    setSingleDeliveryShipment(null);
    setSingleDeliveryResult(null);
    setDeliverySuccessMessage(t('customer.deliveryLocationUpdated'));
    fetchDashboard();
  };

  // Fallback user from context/localStorage for welcome text before dashboard loads
  const getUserDisplayName = () => {
    const guest = t('customer.guestName');
    if (dashboard && dashboard.profile && dashboard.profile.user) {
      const u = dashboard.profile.user;
      return getLocalizedUserFullName(u, i18n.language) || guest;
    }
    if (user) return getLocalizedUserFullName(user, i18n.language) || guest;
    try {
      const stored = localStorage.getItem('user_data');
      if (stored) {
        const parsed = JSON.parse(stored);
        return getLocalizedUserFullName(parsed, i18n.language) || guest;
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('user_data parse', err);
    }
    return guest;
  };

  // Colorful design tokens
  const pageBg = '#f0f4f8';
  const cardBg = '#ffffff';
  const cardBorder = '1px solid #e2e8f0';
  const cardShadow = '0 2px 8px rgba(0,0,0,0.06)';
  const cardRadius = '16px';
  const textPrimary = '#1e293b';
  const textSecondary = '#64748b';
  const textMuted = '#94a3b8';
  // Distinct colors per section (icon bg, icon color)
  const colors = {
    welcomeGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    calendar: { bg: '#dbeafe', icon: '#2563eb' },
    trending: { bg: '#dcfce7', icon: '#16a34a' },
    payments: { bg: '#fff7ed', icon: '#ea580c' },
    profile: { bg: '#ede9fe', icon: '#7c3aed' },
    shipments: { bg: '#e0e7ff', icon: '#4f46e5' },
    invoices: { bg: '#d1fae5', icon: '#059669' },
  };
  // Very light, distinct colors per period
  const periodColors = {
    thisMonth: { bg: '#f0fdfa', label: '#2dd4bf' },      // very light teal
    last3Months: { bg: '#fefce8', label: '#eab308' },   // very light yellow
    last6Months: { bg: '#faf5ff', label: '#a78bfa' },   // very light violet
    thisYear: { bg: '#eef2ff', label: '#818cf8' },      // very light indigo
  };

  if (loading && !dashboard) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
        <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: cardRadius, mb: 3, bgcolor: cardBg }} />
            <Grid container spacing={2}>
              {[1, 2, 3].map((i) => (
                <Grid key={i} xs={12} sm={4}>
                  <Skeleton variant="rectangular" height={110} sx={{ borderRadius: cardRadius, bgcolor: cardBg }} />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: cardRadius, bgcolor: cardBg }} />
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
      <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        <Container maxWidth="xl">
          {/* Welcome Header - colorful gradient */}
          <Box
            sx={{
              background: settings && settings.welcome_background
                ? `linear-gradient(135deg, rgba(102,126,234,0.92) 0%, rgba(118,75,162,0.92) 100%), url(${settings.welcome_background})`
                : colors.welcomeGradient,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: cardRadius,
              padding: { xs: 3, md: 4 },
              marginBottom: 3,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(102,126,234,0.25)',
            }}
          >
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', zIndex: 0 }} />
            <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', zIndex: 0 }} />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography
                  level="h2"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    marginBottom: 0.5,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    letterSpacing: '-0.02em',
                  }}
                >
                  {t('customer.welcome', { name: getUserDisplayName() })}
                </Typography>
                <Typography level="body1" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' }}>
                  {t('customer.yourDashboard', { site: siteName })}
                </Typography>
              </Box>
              <Button
                variant="soft"
                size="sm"
                startDecorator={<RefreshIcon />}
                onClick={fetchDashboard}
                loading={loading}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.35)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.35)', color: 'white' },
                }}
              >
                {t('actions.refresh')}
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert
              color="danger"
              variant="soft"
              sx={{
                mb: 3,
                borderRadius: '12px',
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
              }}
              endDecorator={
                <Button variant="soft" color="danger" size="sm" onClick={fetchDashboard}>
                  {t('actions.retry')}
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {dashboard && (
            <>
              {/* Quick Stats - Shipment counts (ordered: month → 3mo → 6mo → year) + Amount due (not_paid_amount) */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid xs={12} lg={8}>
                  <Card variant="plain" sx={{ bgcolor: cardBg, boxShadow: cardShadow, borderRadius: cardRadius, border: cardBorder, height: '100%' }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: colors.shipments.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <LocalShippingIcon sx={{ color: colors.shipments.icon, fontSize: 22 }} />
                        </Box>
                        <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>{t('customer.shipmentCounts')}</Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid xs={6} md={3}>
                          <Box sx={{ p: 2, borderRadius: '12px', bgcolor: periodColors.thisMonth.bg, height: '100%', textAlign: 'center' }}>
                            <Typography level="body2" sx={{ color: periodColors.thisMonth.label, fontWeight: 600, fontSize: '0.8125rem' }}>{t('customer.thisMonth')}</Typography>
                            <Typography level="h3" sx={{ fontWeight: 700, color: textPrimary, fontSize: '1.5rem', mt: 0.5 }}>
                              {(dashboard.quick_stats && dashboard.quick_stats.shipments_this_month) !== undefined && dashboard.quick_stats.shipments_this_month !== null ? dashboard.quick_stats.shipments_this_month : 0}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid xs={6} md={3}>
                          <Box sx={{ p: 2, borderRadius: '12px', bgcolor: periodColors.last3Months.bg, height: '100%', textAlign: 'center' }}>
                            <Typography level="body2" sx={{ color: periodColors.last3Months.label, fontWeight: 600, fontSize: '0.8125rem' }}>{t('customer.last3Months')}</Typography>
                            <Typography level="h3" sx={{ fontWeight: 700, color: textPrimary, fontSize: '1.5rem', mt: 0.5 }}>
                              {(dashboard.quick_stats && dashboard.quick_stats.shipments_3_months) !== undefined && dashboard.quick_stats.shipments_3_months !== null ? dashboard.quick_stats.shipments_3_months : 0}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid xs={6} md={3}>
                          <Box sx={{ p: 2, borderRadius: '12px', bgcolor: periodColors.last6Months.bg, height: '100%', textAlign: 'center' }}>
                            <Typography level="body2" sx={{ color: periodColors.last6Months.label, fontWeight: 600, fontSize: '0.8125rem' }}>{t('customer.last6Months')}</Typography>
                            <Typography level="h3" sx={{ fontWeight: 700, color: textPrimary, fontSize: '1.5rem', mt: 0.5 }}>
                              {(dashboard.quick_stats && dashboard.quick_stats.shipments_6_months) !== undefined && dashboard.quick_stats.shipments_6_months !== null ? dashboard.quick_stats.shipments_6_months : 0}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid xs={6} md={3}>
                          <Box sx={{ p: 2, borderRadius: '12px', bgcolor: periodColors.thisYear.bg, height: '100%', textAlign: 'center' }}>
                            <Typography level="body2" sx={{ color: periodColors.thisYear.label, fontWeight: 600, fontSize: '0.8125rem' }}>{t('customer.thisYear')}</Typography>
                            <Typography level="h3" sx={{ fontWeight: 700, color: textPrimary, fontSize: '1.5rem', mt: 0.5 }}>
                              {(dashboard.quick_stats && dashboard.quick_stats.shipments_this_year) !== undefined && dashboard.quick_stats.shipments_this_year !== null ? dashboard.quick_stats.shipments_this_year : 0}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid xs={12} lg={4}>
                  <Card variant="plain" sx={{ bgcolor: cardBg, boxShadow: cardShadow, borderRadius: cardRadius, border: cardBorder, height: '100%' }}>
                    <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { lg: 180 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: colors.payments.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PaymentsIcon sx={{ color: colors.payments.icon, fontSize: 22 }} />
                        </Box>
                        <Typography level="body2" sx={{ color: textSecondary, fontWeight: 500 }}>{t('customer.amountDue')}</Typography>
                      </Box>
                      <Typography level="h3" sx={{ fontWeight: 700, color: textPrimary, fontSize: '1.75rem' }}>
                        <OmrAmount
                          value={
                            (dashboard.quick_stats && dashboard.quick_stats.not_paid_amount != null)
                              ? dashboard.quick_stats.not_paid_amount
                              : (dashboard.financial_summary && dashboard.financial_summary.total_outstanding)
                          }
                        />
                      </Typography>
                      <Typography level="body2" sx={{ color: textMuted, mt: 0.25 }}>{t('customer.amountDueSubtitle')}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Profile Card - above Shipments */}
              <Grid container spacing={3} sx={{ mb: 0 }}>
                <Grid xs={12}>
                  <Card variant="plain" sx={{ bgcolor: cardBg, borderRadius: cardRadius, boxShadow: cardShadow, border: cardBorder }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: colors.profile.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PersonIcon sx={{ color: colors.profile.icon, fontSize: 24 }} />
                          </Box>
                          <Box>
                            <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>{t('customer.profile')}</Typography>
                            <Typography level="body2" sx={{ color: textMuted, mt: 0.25 }}>{t('customer.yourAccountDetails')}</Typography>
                          </Box>
                        </Box>
                        {dashboard.profile && dashboard.profile.is_profile_complete && (
                          <Chip size="sm" color="success" variant="soft" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 500 }}>{t('customer.complete')}</Chip>
                        )}
                      </Box>
                      {dashboard.profile && dashboard.profile.user && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>{t('actions.name')}</Typography>
                            <Typography level="body1" sx={{ color: textPrimary, fontWeight: 600 }}>
                              {getLocalizedUserFullName(dashboard.profile.user, i18n.language) || '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>{t('actions.phone')}</Typography>
                            <Typography level="body1" sx={{ color: textPrimary }}>{dashboard.profile.user.phone_number || '—'}</Typography>
                          </Box>
                          {dashboard.profile.user.id_number && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', py: 1.5 }}>
                              <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>{t('customer.civilId')}</Typography>
                              <Typography level="body1" sx={{ color: textPrimary }}>{dashboard.profile.user.id_number}</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      {dashboard.profile && !dashboard.profile.is_profile_complete && (
                        <Button
                          variant="soft"
                          color="primary"
                          size="sm"
                          fullWidth
                          onClick={() => navigate('/customer-profile')}
                          sx={{ mt: 2, borderRadius: '10px', fontWeight: 500 }}
                        >
                          {t('customer.completeProfile')}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Shipments Summary - below Profile */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid xs={12}>
                  <Card variant="plain" sx={{ bgcolor: cardBg, borderRadius: cardRadius, boxShadow: cardShadow, border: cardBorder }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: colors.shipments.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <LocalShippingIcon sx={{ color: colors.shipments.icon, fontSize: 22 }} />
                          </Box>
                          <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>{t('customer.shipments')}</Typography>
                        </Box>
                        <Button variant="plain" size="sm" onClick={() => navigate('/customer-shipments')} sx={{ color: primaryColor, fontWeight: 500 }}>
                          {t('customer.viewAll')}
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        <Chip variant="soft" size="sm" sx={{ bgcolor: colors.shipments.bg, color: colors.shipments.icon, fontWeight: 600 }}>
                          {t('customer.totalLabel')} {(dashboard.shipments_summary && dashboard.shipments_summary.total) != null ? dashboard.shipments_summary.total : 0}
                        </Chip>
                        <Chip variant="soft" size="sm" sx={{ bgcolor: colors.shipments.bg, color: colors.shipments.icon, fontWeight: 600 }}>
                          {t('customer.notDelivered')} {(dashboard.shipments_summary && dashboard.shipments_summary.active) != null ? dashboard.shipments_summary.active : 0}
                        </Chip>
                        {SHIPMENT_STATUS_KEYS.map((statusKey) => {
                          const count = getShipmentStatusCount(dashboard.shipments_summary, statusKey);
                          const style = shipmentStatusChipStyle(statusKey);
                          return (
                            <Chip
                              key={statusKey}
                              variant="soft"
                              size="sm"
                              sx={{ bgcolor: style.bg, color: style.color, fontWeight: 500 }}
                            >
                              {formatStatus(statusKey)}: {count}
                            </Chip>
                          );
                        })}
                      </Box>
                      <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 2, fontSize: '0.875rem' }}>{t('customer.recentShipments')}</Typography>
                      {(!dashboard.shipments_summary || !dashboard.shipments_summary.recent_shipments || dashboard.shipments_summary.recent_shipments.length === 0) ? (
                        <Box sx={{ py: 4, px: 2, borderRadius: '16px', bgcolor: '#fafbfc', border: '1px dashed #e5e7eb', textAlign: 'center' }}>
                          <LocalShippingIcon sx={{ fontSize: 40, color: textMuted, opacity: 0.6, mb: 1 }} />
                          <Typography level="body2" sx={{ color: textMuted }}>{t('customer.noRecentShipments')}</Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          {dashboard.shipments_summary.recent_shipments.map((s) => {
                            const statusStyle = shipmentStatusChipStyle(s.status);
                            return (
                              <Grid key={s.id} xs={12} sm={6} md={4}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    height: '100%',
                                    borderRadius: '16px',
                                    bgcolor: '#fafbfc',
                                    border: '1px solid #f1f3f5',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                    transition: 'box-shadow 0.2s, border-color 0.2s',
                                    '&:hover': {
                                      borderColor: '#e5e7eb',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
                                    <Typography level="body1" sx={{ fontWeight: 600, color: textPrimary, fontSize: '0.9375rem' }}>
                                      {t('customer.shipmentNumber', { id: s.id })}
                                    </Typography>
                                    <Chip
                                      size="sm"
                                      variant="soft"
                                      sx={{
                                        bgcolor: statusStyle.bg,
                                        color: statusStyle.color,
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      {formatStatus(s.status)}
                                    </Chip>
                                  </Box>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography level="body2" sx={{ color: textMuted, fontSize: '0.8125rem', mb: 0.25 }}>
                                      <strong style={{ color: textSecondary }}>{t('customer.ref')}</strong> {s.shipper_reference || '—'}
                                    </Typography>
                                    <Typography level="body2" sx={{ color: textMuted, fontSize: '0.8125rem' }}>
                                      <strong style={{ color: textSecondary }}>{t('customer.tracking')}</strong> {s.tracking_number || '—'}
                                    </Typography>
                                    {isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields) && getCustomerShipmentUnitSerial(s) ? (
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.8125rem' }}>
                                        <strong style={{ color: textSecondary }}>{t('customer.unit')}</strong> {getCustomerShipmentUnitSerial(s)}
                                      </Typography>
                                    ) : null}
                                    {isCustomerShipmentUnitFieldVisible('expected_warehouse_eta', unitVisibleFields) && getCustomerShipmentUnit(s)?.expected_warehouse_eta ? (
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.8125rem' }}>
                                        <strong style={{ color: textSecondary }}>{t('customer.quickView.expectedWarehouseEta')}</strong>{' '}
                                        {formatCustomerShipmentUnitDate(getCustomerShipmentUnit(s).expected_warehouse_eta)}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                  {s.items_description && (
                                    <Typography level="body2" sx={{ color: textSecondary, fontSize: '0.8125rem', mb: 1.5, lineHeight: 1.4 }}>
                                      {s.items_description}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
                                    {s.created_at && (
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>
                                        {formatDate(s.created_at)}
                                      </Typography>
                                    )}
                                    {s.current_location && (
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }} noWrap>
                                        · {s.current_location}
                                      </Typography>
                                    )}
                                  </Box>
                                  {(s.price != null && s.price !== '') && (
                                    <Typography level="body2" sx={{ color: textPrimary, fontSize: '0.8125rem', fontWeight: 600, mb: 1.5 }}>
                                      <OmrAmount value={s.price} />
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    <IconButton
                                      variant="soft"
                                      size="sm"
                                      color="neutral"
                                      onClick={() => setQuickViewShipment(s)}
                                      title={t('customer.quickViewButtonTitle')}
                                      sx={{ borderRadius: '8px' }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    <Button
                                      variant="soft"
                                      size="sm"
                                      color="neutral"
                                      startDecorator={<LocationOnIcon />}
                                      onClick={() => openSingleDeliveryModal(s)}
                                      disabled={cannotUpdateDeliveryLocation(s.status)}
                                      title={cannotUpdateDeliveryLocation(s.status) ? t('customer.cannotUpdateDelivery') : t('customer.updateDeliveryLocation')}
                                      sx={{ borderRadius: '8px', fontWeight: 500 }}
                                    >
                                      {t('customer.delivery')}
                                    </Button>
                                    <Button
                                      variant="soft"
                                      color="primary"
                                      size="sm"
                                      startDecorator={<VisibilityIcon />}
                                      onClick={() => navigate(`/customer-shipment/${s.id}/tracking`)}
                                      sx={{ borderRadius: '8px', fontWeight: 500 }}
                                    >
                                      {t('customer.track')}
                                    </Button>
                                  </Box>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Financial Summary - Invoices & payments */}
              <Card variant="plain" sx={{ bgcolor: cardBg, borderRadius: cardRadius, boxShadow: cardShadow, border: cardBorder, mt: 3 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: '14px', bgcolor: colors.invoices.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ReceiptIcon sx={{ color: colors.invoices.icon, fontSize: 22 }} />
                      </Box>
                      <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>{t('customer.invoicesAndPayments')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button variant="plain" size="sm" onClick={() => navigate('/customer-invoices')} sx={{ color: primaryColor, fontWeight: 500 }}>
                        {t('customer.viewAllInvoices')}
                      </Button>
                      <Button variant="plain" size="sm" onClick={() => navigate('/customer-payments')} sx={{ color: primaryColor, fontWeight: 500 }}>
                        {t('customer.myPayments')}
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip size="sm" variant="soft" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 500 }}>{t('customer.unpaidChip')} {(dashboard.financial_summary && dashboard.financial_summary.unpaid_invoices_count) != null ? dashboard.financial_summary.unpaid_invoices_count : 0}</Chip>
                    <Typography level="body2" sx={{ color: textSecondary }}>
                      {t('customer.outstanding')} <strong style={{ color: textPrimary }}><OmrAmount value={dashboard.financial_summary && dashboard.financial_summary.total_outstanding} /></strong>
                    </Typography>
                  </Box>

                  {/* Unpaid invoices list */}
                  <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 2, fontSize: '0.875rem' }}>{t('customer.unpaidInvoices')}</Typography>
                  {(!dashboard.financial_summary || !dashboard.financial_summary.unpaid_invoices || dashboard.financial_summary.unpaid_invoices.length === 0) ? (
                    <Box sx={{ py: 3, px: 2, borderRadius: '16px', bgcolor: '#fafbfc', border: '1px dashed #e5e7eb', textAlign: 'center', mb: 3 }}>
                      <ReceiptIcon sx={{ fontSize: 36, color: textMuted, opacity: 0.6, mb: 1 }} />
                      <Typography level="body2" sx={{ color: textMuted }}>{t('customer.noUnpaidInvoices')}</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {dashboard.financial_summary.unpaid_invoices.map((inv) => {
                        const shipments = inv.shipments || [];
                        const total = shipments.reduce((sum, s) => sum + parseFloat(s.price || 0), 0);
                        const paymentChipStyle = getPaymentStatusChipStyle(inv.payment_status);
                        return (
                          <Grid key={inv.id} xs={12} md={6}>
                            <Box
                              sx={{
                                p: 2.5,
                                height: '100%',
                                borderRadius: '16px',
                                bgcolor: '#fffaf5',
                                border: '1px solid #fed7aa',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                transition: 'box-shadow 0.2s, border-color 0.2s',
                                '&:hover': {
                                  borderColor: '#fdba74',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
                                <Typography level="body1" sx={{ fontWeight: 600, color: textPrimary, fontSize: '0.9375rem' }}>
                                  {t('customer.invoiceNumber', { id: inv.id })}
                                </Typography>
                                <Chip size="sm" variant="soft" sx={{ fontWeight: 500, textTransform: 'none', ...paymentChipStyle }}>
                                  {formatPaymentStatus(inv.payment_status)}
                                </Chip>
                              </Box>
                              <Typography level="body2" sx={{ color: textPrimary, fontWeight: 600, fontSize: '1rem', mb: 1.5 }}>
                                {t('customer.totalColon')} <OmrAmount value={total} />
                              </Typography>
                              {inv.amount_due != null && (
                                <Typography level="body2" sx={{ color: textSecondary, fontSize: '0.8125rem', mb: 1 }}>
                                  {t('customer.amountDue')}: <OmrAmount value={inv.amount_due} />
                                </Typography>
                              )}
                              {inv.created_at && (
                                <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem', mb: 1.5 }}>
                                  {t('customer.createdLabel')} {formatDate(inv.created_at)}
                                </Typography>
                              )}
                              {shipments.length > 0 && (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem', fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                    {t('customer.shipmentsSection')}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {shipments.map((s) => (
                                      <Box
                                        key={s.id}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          justifyContent: 'space-between',
                                          flexWrap: 'wrap',
                                          gap: 0.5,
                                          py: 0.75,
                                          px: 1.25,
                                          borderRadius: '10px',
                                          bgcolor: '#ffffff',
                                          border: '1px solid #f1f3f5',
                                        }}
                                      >
                                        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                                          <Typography level="body2" sx={{ color: textSecondary, fontSize: '0.8125rem' }}>
                                            <strong style={{ color: textPrimary }}>{t('customer.idLabel')}</strong> {s.id} · <strong style={{ color: textPrimary }}>{t('customer.ref')}</strong> {s.shipper_reference || '—'} · <strong style={{ color: textPrimary }}>{t('customer.tracking')}</strong> {s.tracking_number || '—'}
                                            {isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields) && getCustomerShipmentUnitSerial(s) ? (
                                              <> · <strong style={{ color: textPrimary }}>{t('customer.unit')}</strong> {getCustomerShipmentUnitSerial(s)}</>
                                            ) : null}
                                          </Typography>
                                        </Box>
                                        <Typography level="body2" sx={{ color: textPrimary, fontSize: '0.8125rem', fontWeight: 600, flexShrink: 0 }}>
                                          <OmrAmount value={s.price} />
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                              <Box sx={{ mt: 0.5 }}>
                                <CustomerInvoicePayActions
                                  invoice={inv}
                                  needsRegenerate={needsRegenerate}
                                  regenerating={regeneratingInvoiceId === inv.id}
                                  onRegenerate={() => {
                                    setOpenPaymentLinkAfterRefresh(inv.id);
                                    handleRegenerateInvoice(inv.id);
                                  }}
                                  onAfterPayFull={async () => {
                                    await fetchDashboard();
                                  }}
                                  size="sm"
                                  fullWidth
                                  showStartDecorator={false}
                                  hidePayFullWhenSingleLink
                                />
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}

                  <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 2, fontSize: '0.875rem' }}>{t('customer.recentInvoices')}</Typography>
                  {(!dashboard.financial_summary || !dashboard.financial_summary.recent_invoices || dashboard.financial_summary.recent_invoices.length === 0) ? (
                    <Box sx={{ py: 4, px: 2, borderRadius: '16px', bgcolor: '#fafbfc', border: '1px dashed #e5e7eb', textAlign: 'center' }}>
                      <ReceiptIcon sx={{ fontSize: 40, color: textMuted, opacity: 0.6, mb: 1 }} />
                      <Typography level="body2" sx={{ color: textMuted }}>{t('customer.noRecentInvoices')}</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {dashboard.financial_summary.recent_invoices.map((inv) => {
                        const shipments = inv.shipments || [];
                        const total = shipments.reduce((sum, s) => sum + parseFloat(s.price || 0), 0);
                        const paymentChipStyle = getPaymentStatusChipStyle(inv.payment_status);
                        return (
                          <Grid key={inv.id} xs={12} md={6}>
                            <Box
                              sx={{
                                p: 2.5,
                                height: '100%',
                                borderRadius: '16px',
                                bgcolor: '#fafbfc',
                                border: '1px solid #f1f3f5',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                transition: 'box-shadow 0.2s, border-color 0.2s',
                                '&:hover': {
                                  borderColor: '#e5e7eb',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
                                <Typography level="body1" sx={{ fontWeight: 600, color: textPrimary, fontSize: '0.9375rem' }}>
                                  {t('customer.invoiceNumber', { id: inv.id })}
                                </Typography>
                                <Chip size="sm" variant="soft" sx={{ fontWeight: 500, textTransform: 'none', ...paymentChipStyle }}>
                                  {formatPaymentStatus(inv.payment_status)}
                                </Chip>
                              </Box>
                              <Typography level="body2" sx={{ color: textPrimary, fontWeight: 600, fontSize: '1rem', mb: 1.5 }}>
                                {t('customer.totalColon')} <OmrAmount value={total} />
                              </Typography>
                              {inv.amount_due != null && Number(inv.amount_due) > 0 && (
                                <Typography level="body2" sx={{ color: textSecondary, fontSize: '0.8125rem', mb: 1 }}>
                                  {t('customer.amountDue')}: <OmrAmount value={inv.amount_due} />
                                </Typography>
                              )}
                              {shipments.length > 0 && (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem', fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                    {t('customer.shipmentsSection')}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {shipments.map((s) => {
                                      const shipPaymentStatus = s.payment_status != null ? s.payment_status : inv.payment_status;
                                      const shipChipStyle = getPaymentStatusChipStyle(shipPaymentStatus);
                                      return (
                                        <Box
                                          key={s.id}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 0.5,
                                            py: 0.75,
                                            px: 1.25,
                                            borderRadius: '10px',
                                            bgcolor: '#ffffff',
                                            border: '1px solid #f1f3f5',
                                          }}
                                        >
                                          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                                            <Typography level="body2" sx={{ color: textSecondary, fontSize: '0.8125rem' }}>
                                              <strong style={{ color: textPrimary }}>{t('customer.idLabel')}</strong> {s.id} · <strong style={{ color: textPrimary }}>{t('customer.ref')}</strong> {s.shipper_reference || '—'} · <strong style={{ color: textPrimary }}>{t('customer.tracking')}</strong> {s.tracking_number || '—'}
                                              {isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields) && getCustomerShipmentUnitSerial(s) ? (
                                                <> · <strong style={{ color: textPrimary }}>{t('customer.unit')}</strong> {getCustomerShipmentUnitSerial(s)}</>
                                              ) : null}
                                            </Typography>
                                            <Chip size="sm" variant="soft" sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.6875rem', py: 0.25, ...shipChipStyle }}>
                                              {formatPaymentStatus(shipPaymentStatus)}
                                            </Chip>
                                          </Box>
                                          <Typography level="body2" sx={{ color: textPrimary, fontSize: '0.8125rem', fontWeight: 600, flexShrink: 0 }}>
                                            <OmrAmount value={s.price} />
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              )}
                              <Box sx={{ mt: 0.5 }}>
                                <CustomerInvoicePayActions
                                  invoice={inv}
                                  needsRegenerate={needsRegenerate}
                                  regenerating={regeneratingInvoiceId === inv.id}
                                  onRegenerate={() => {
                                    setOpenPaymentLinkAfterRefresh(inv.id);
                                    handleRegenerateInvoice(inv.id);
                                  }}
                                  onAfterPayFull={async () => {
                                    await fetchDashboard();
                                  }}
                                  size="sm"
                                  fullWidth
                                  showStartDecorator={false}
                                  hidePayFullWhenSingleLink
                                />
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Quick view modal */}
          <Modal open={!!quickViewShipment} onClose={() => setQuickViewShipment(null)}>
            <ModalDialog
              sx={{
                maxWidth: 560,
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                borderRadius: cardRadius,
                p: 2.5,
              }}
            >
              <ModalClose />
              {quickViewShipment && (
                <>
                  <Typography level="h5" sx={{ mb: 2, fontWeight: 700 }}>
                    {t('customer.shipmentNumber', { id: quickViewShipment.id })}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[
                      { fieldKey: 'shipperReference', value: quickViewShipment.shipper_reference || '—' },
                      { fieldKey: 'trackingNumber', value: quickViewShipment.tracking_number || '—' },
                      ...(isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields)
                        ? [{ fieldKey: 'unitSerial', value: getCustomerShipmentUnitSerial(quickViewShipment) || '—' }]
                        : []),
                      ...(isCustomerShipmentUnitFieldVisible('expected_destination_eta', unitVisibleFields) && getCustomerShipmentUnit(quickViewShipment)?.expected_destination_eta
                        ? [{ fieldKey: 'expectedDestinationEta', value: formatCustomerShipmentUnitDate(getCustomerShipmentUnit(quickViewShipment).expected_destination_eta) }]
                        : []),
                      ...(isCustomerShipmentUnitFieldVisible('expected_warehouse_eta', unitVisibleFields) && getCustomerShipmentUnit(quickViewShipment)?.expected_warehouse_eta
                        ? [{ fieldKey: 'expectedWarehouseEta', value: formatCustomerShipmentUnitDate(getCustomerShipmentUnit(quickViewShipment).expected_warehouse_eta) }]
                        : []),
                      { fieldKey: 'itemsDescription', value: quickViewShipment.items_description || '—' },
                      { fieldKey: 'packagesQuantity', value: quickViewShipment.packages_quantity != null ? String(quickViewShipment.packages_quantity) : '—' },
                      { fieldKey: 'weight', value: quickViewShipment.weight != null && quickViewShipment.weight !== '' ? String(quickViewShipment.weight) : '—' },
                      { fieldKey: 'dimensions', value: [quickViewShipment.width_cm, quickViewShipment.length_cm, quickViewShipment.height_cm].every((v) => v != null && v !== '') ? `${quickViewShipment.width_cm} × ${quickViewShipment.length_cm} × ${quickViewShipment.height_cm} cm` : '—' },
                      { fieldKey: 'volumetricWeight', value: quickViewShipment.volumetric_weight != null && quickViewShipment.volumetric_weight !== '' ? String(quickViewShipment.volumetric_weight) : '—' },
                      { fieldKey: 'price', value: quickViewShipment.price != null && quickViewShipment.price !== '' ? <OmrAmount value={quickViewShipment.price} /> : '—' },
                      { fieldKey: 'location', value: quickViewShipment.location_name || (quickViewShipment.location != null && quickViewShipment.location !== '' ? String(quickViewShipment.location) : '—') },
                      { fieldKey: 'status', value: formatStatus(quickViewShipment.status), variant: 'status' },
                      { fieldKey: 'eventType', value: quickViewShipment.event_type ? labelForEventTypeCode(quickViewShipment.event_type) : '—' },
                      { fieldKey: 'paymentStatus', value: quickViewShipment.payment_status ? formatPaymentStatus(quickViewShipment.payment_status) : '—', variant: 'paymentStatus' },
                      { fieldKey: 'invoiced', value: quickViewShipment.invoiced != null ? (quickViewShipment.invoiced ? t('actions.yes') : t('actions.no')) : '—' },
                      { fieldKey: 'note', value: quickViewShipment.note || '—' },
                      { fieldKey: 'created', value: formatDateTime(quickViewShipment.created_at) },
                      { fieldKey: 'modified', value: formatDateTime(quickViewShipment.modified_at) },
                    ].map(({ fieldKey, value, variant }) => {
                      const label = t(`customer.quickView.${fieldKey}`);
                      const isStatus = variant === 'status';
                      const isPaymentStatus = variant === 'paymentStatus';
                      return (
                        <Grid key={fieldKey} xs={12} sm={6}>
                          <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 0.25 }}>
                            {label}
                          </Typography>
                          {isStatus ? (
                            <Chip
                              size="sm"
                              variant="soft"
                              sx={{
                                mt: 0.25,
                                bgcolor: shipmentStatusChipStyle(quickViewShipment.status).bg,
                                color: shipmentStatusChipStyle(quickViewShipment.status).color,
                                fontWeight: 500,
                                textTransform: 'none',
                              }}
                            >
                              {formatStatus(quickViewShipment.status)}
                            </Chip>
                          ) : isPaymentStatus ? (
                            <Chip
                              size="sm"
                              variant="soft"
                              sx={{
                                mt: 0.25,
                                fontWeight: 500,
                                textTransform: 'none',
                                ...getPaymentStatusChipStyle(quickViewShipment.payment_status),
                              }}
                            >
                              {quickViewShipment.payment_status ? formatPaymentStatus(quickViewShipment.payment_status) : '—'}
                            </Chip>
                          ) : (
                            <Typography level="body1" sx={{ color: textPrimary }}>
                              {value}
                            </Typography>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                  {quickViewShipment.receiver_details && (
                    <>
                      <Typography level="title-sm" sx={{ color: textSecondary, fontWeight: 600, mb: 1, mt: 1 }}>
                        {t('customer.receiverDetailsBlock')}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {[
                          { rk: 'receiverName', value: quickViewShipment.receiver_details.receiver_name || '—' },
                          { rk: 'receiverPhone', value: quickViewShipment.receiver_details.receiver_phone_number || '—' },
                          { rk: 'receiverCivilId', value: quickViewShipment.receiver_details.receiver_civil_id || '—' },
                          { rk: 'destinationTypeForm', value: formatDestinationLocationType(quickViewShipment.receiver_details.destination_location_type) },
                          { rk: 'destinationField', value: quickViewShipment.receiver_details.destination_location || '—' },
                        ].map(({ rk, value }) => (
                          <Grid key={rk} xs={12} sm={6}>
                            <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 0.25 }}>
                              {t(`customer.${rk}`)}
                            </Typography>
                            <Typography level="body1" sx={{ color: textPrimary }}>
                              {value}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                  {!quickViewShipment.receiver_details && (
                    <Typography level="body2" sx={{ color: textSecondary, mb: 2 }}>
                      {t('customer.noReceiverDetails')}
                    </Typography>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="plain" onClick={() => setQuickViewShipment(null)}>
                      {t('actions.close')}
                    </Button>
                    <Button
                      variant="soft"
                      color="primary"
                      startDecorator={<VisibilityIcon />}
                      onClick={() => {
                        setQuickViewShipment(null);
                        navigate(`/customer-shipment/${quickViewShipment.id}/tracking`);
                      }}
                    >
                      {t('customer.trackShipment')}
                    </Button>
                  </Box>
                </>
              )}
            </ModalDialog>
          </Modal>

          {/* Single shipment delivery location modal */}
          <Modal open={!!singleDeliveryShipment} onClose={() => !singleDeliverySubmitting && setSingleDeliveryShipment(null)}>
            <ModalDialog
              sx={{
                maxWidth: 480,
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                borderRadius: cardRadius,
                p: 2.5,
              }}
            >
              <ModalClose disabled={singleDeliverySubmitting} />
              {singleDeliveryShipment && (
                <>
                  <Typography level="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    {t('customer.deliveryLocationTitle', { id: singleDeliveryShipment.id })}
                  </Typography>
                  <Typography level="body2" sx={{ color: textSecondary, mb: 2 }}>
                    {t('customer.deliveryDescription')}
                  </Typography>
                  <CustomerDeliveryDestinationForm
                    destinationType={singleDeliveryType}
                    onDestinationTypeChange={setSingleDeliveryType}
                    destinationLocation={singleDeliveryLocation}
                    onDestinationLocationChange={setSingleDeliveryLocation}
                    siteName={siteName}
                    disabled={singleDeliverySubmitting}
                  />
                  {singleDeliveryResult && singleDeliveryResult.error && (
                    <Alert color="danger" variant="soft" sx={{ mb: 2, borderRadius: '10px' }}>
                      {singleDeliveryResult.error}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                      variant="plain"
                      onClick={() => !singleDeliverySubmitting && setSingleDeliveryShipment(null)}
                      disabled={singleDeliverySubmitting}
                    >
                      {t('actions.cancel')}
                    </Button>
                    <Button
                      variant="soft"
                      color="primary"
                      onClick={handleSingleDeliverySubmit}
                      loading={singleDeliverySubmitting}
                      startDecorator={<LocationOnIcon />}
                      sx={{ borderRadius: '10px' }}
                    >
                      {t('customer.updateLocation')}
                    </Button>
                  </Box>
                </>
              )}
            </ModalDialog>
          </Modal>
        </Container>
      </Box>

      <Snackbar
        autoHideDuration={5000}
        open={!!deliverySuccessMessage}
        variant="soft"
        color="success"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setDeliverySuccessMessage(null);
        }}
      >
        {deliverySuccessMessage}
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={regenerateSnackbar.open}
        variant="soft"
        color={regenerateSnackbar.color}
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setRegenerateSnackbar((s) => ({ ...s, open: false }));
        }}
      >
        {regenerateSnackbar.message}
      </Snackbar>
    </Box>
  );
}
